import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, Crown, CreditCard, Loader, Calendar, Clock } from 'lucide-react';
import DoctorSummaryCard from '../../DoctorSummaryCard';
import { getSpecialtyPrice, getMembershipPlans, getDurationMinutes, getExtraConsultationPrice } from '../../../utils/checkoutHelpers';
import { fetchVisibleMemberships } from '../../../services/FirebaseService';

const PLAN_MONTHS = { monthly: 1, quarterly: 3, annual: 12 };

const buildFallbackPlans = () =>
  Object.entries(getMembershipPlans(getSpecialtyPrice())).map(([key, p]) => ({
    id: p.odooId,
    price: p.price,
    months: PLAN_MONTHS[key] || 1,
    monthlyPrice: Math.round(p.price / (PLAN_MONTHS[key] || 1)),
  }));

// ─────────────────────────────────────────────────────────────────────────────
// MembershipOffer — hoja "¿Cómo quieres agendar?" (paridad con la app Flutter,
// `_WithoutMembershipBranch` de orchestrated_slot_sheet.dart).
//
// El usuario sin membresía elige UNA de dos opciones sobre el MISMO horario que
// seleccionó: comprar membresía (recomendado) o pagar esa única cita. No hay
// "horarios alternativos" ni concepto de "consulta extra" a nivel de producto:
// pagar una cita sin membresía es simplemente eso.
// ─────────────────────────────────────────────────────────────────────────────
const MembershipOffer = ({
  selectedSpecialty,
  specialtyConfig,
  selectedSlot,
  selectedDoctor,
  selectedDate,
  selectedTime,
  userData,
  onContinueToCheckout,
  onBack,
  isCreatingUser,
}) => {
  const specialty = specialtyConfig[selectedSpecialty] || {};

  // Precio sin membresía de ESTA cita (según la duración del slot). Sólo se usa
  // para estimar el ahorro de la membresía; el precio real lo confirma el
  // checkout contra el backend.
  const durationId = selectedSlot?.duration_id || specialty.duration;
  const minutes = getDurationMinutes(specialty, durationId);
  const singlePrice =
    getExtraConsultationPrice(specialty, minutes, false) ?? getSpecialtyPrice();

  // Mejor ahorro mensual vs. pagar cada cita suelta (para el badge "Ahorra hasta X%").
  const [plans, setPlans] = useState(buildFallbackPlans);
  useEffect(() => {
    let cancelled = false;
    fetchVisibleMemberships()
      .then((list) => {
        if (!cancelled && Array.isArray(list) && list.length > 0) setPlans(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const maxSavingsPct = (() => {
    if (!singlePrice) return 0;
    const cheapestMonthly = plans
      .map((p) => Number(p.monthlyPrice) || Math.round((Number(p.price) || 0) / (p.months || 1)))
      .filter((n) => n > 0)
      .sort((a, b) => a - b)[0];
    if (!cheapestMonthly) return 0;
    return Math.max(0, Math.round((1 - cheapestMonthly / singlePrice) * 100));
  })();

  const busy = isCreatingUser;

  const dateLabel = selectedDate
    ? selectedDate.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' })
    : '';
  const timeLabel = selectedTime || selectedSlot?.init || selectedSlot?.hourStart || '';

  return (
    <div className="max-w-lg mx-auto px-4 py-6 animate-fadeIn">
      <button
        onClick={onBack}
        className="mb-5 text-daisies hover:text-daisies-hover font-medium flex items-center gap-1.5 transition-all text-sm active:scale-[0.97]"
      >
        <ChevronLeft size={16} />
        Volver
      </button>

      {/* Resumen del horario elegido */}
      {selectedDoctor ? (
        <div className="mb-4">
          <DoctorSummaryCard doctor={selectedDoctor} compact />
        </div>
      ) : null}

      <div className="bg-surface-card rounded-card p-4 mb-4 flex items-center gap-4">
        <div className="flex items-center gap-1.5 text-text-secondary text-sm">
          <Calendar size={15} className="text-daisies" />
          <span className="capitalize">{dateLabel}</span>
        </div>
        {timeLabel ? (
          <div className="flex items-center gap-1.5 text-text-secondary text-sm">
            <Clock size={15} className="text-daisies" />
            <span>{timeLabel}</span>
          </div>
        ) : null}
        {minutes ? (
          <span className="ml-auto text-xs font-semibold text-daisies bg-daisies-50 rounded-full px-2.5 py-1">
            {minutes} min
          </span>
        ) : null}
      </div>

      {isCreatingUser && (
        <div className="mb-4 p-4 bg-brand-tint rounded-card flex items-center gap-3">
          <Loader className="animate-spin text-brand" size={18} />
          <p className="text-sm font-medium text-brand-deep">Configurando tu perfil...</p>
        </div>
      )}

      {/* Aviso sin membresía */}
      <div className="bg-brand-tint rounded-card p-4 mb-5 flex items-start gap-3">
        <div className="p-2 bg-daisies-50 text-daisies rounded-input flex-shrink-0">
          <Crown size={20} />
        </div>
        <div>
          <p className="font-bold text-text-primary leading-tight">Aún no tienes membresía</p>
          <p className="text-sm text-text-secondary mt-1">
            Con membresía ahorras en cada cita y desbloqueas beneficios.
          </p>
        </div>
      </div>

      <h2 className="text-xl font-bold text-text-primary mb-3">¿Cómo quieres agendar?</h2>

      {/* Opción 1: membresía (recomendada) */}
      <button
        onClick={() => { if (!busy) onContinueToCheckout({ type: 'membership', hasMembership: true }); }}
        disabled={busy}
        className="press-scale-card w-full text-left p-5 rounded-card bg-daisies text-white transition-all flex items-center gap-4 mb-3 disabled:opacity-60"
      >
        <div className="p-3 bg-white/20 rounded-input flex-shrink-0"><Crown size={20} /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-lg leading-tight">Comprar membresía y agendar</span>
            <span className="text-[11px] font-bold uppercase tracking-wide bg-white/20 rounded-full px-2 py-0.5">
              Recomendado
            </span>
          </div>
          <div className="text-sm text-white/85 mt-1">Ahorra en cada cita y agenda sin pagar de más.</div>
          {maxSavingsPct > 0 && (
            <div className="inline-block mt-2 text-xs font-bold text-mint-deep bg-mint-pill rounded-full px-2.5 py-1">
              Ahorra hasta {maxSavingsPct}%
            </div>
          )}
        </div>
        <ChevronRight size={22} className="text-white flex-shrink-0" />
      </button>

      {/* Opción 2: pagar esta cita */}
      <button
        onClick={() => { if (!busy) onContinueToCheckout({ type: 'single', hasMembership: false }); }}
        disabled={busy}
        className="press-scale-card w-full text-left p-5 rounded-card bg-warm-canvas hover:bg-daisies-50 transition-all flex items-center gap-4 disabled:opacity-60"
      >
        <div className="p-3 bg-daisies-50 text-daisies rounded-input flex-shrink-0"><CreditCard size={20} /></div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-lg leading-tight text-text-primary">Pagar esta cita</div>
          <div className="text-sm text-text-secondary mt-1">Una sola cita, sin compromiso.</div>
        </div>
        <ChevronRight size={22} className="text-daisies flex-shrink-0" />
      </button>
    </div>
  );
};

export default MembershipOffer;
